import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CalendarDays, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useCurrentLeadSheet } from "@/hooks/useCurrentLeadSheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGoogleCalendarSync } from "@/hooks/useGoogleCalendarSync";
import LeadCalendarView from "@/components/LeadCalendarView";
import { getCurrentUser } from "@/lib/authUser";
import { requestWithAuthRecovery } from "@/lib/authRequest";
import { useAuth } from "@/contexts/AuthContext";

interface Lead {
  id: string;
  lead_name: string;
}

interface FloatingCalendarProps {
  onOpenLead?: (leadId: string) => void;
}

export function FloatingCalendar({ onOpenLead }: FloatingCalendarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadPanelWidth, setLeadPanelWidth] = useState(0);
  const { pathname } = useLocation();
  const { workspace, selectedGroupId } = useWorkspace();
  const { initializing } = useAuth();
  const routeGroupId = useMemo(() => {
    const match = pathname.match(/\/dashboard\/groups\/([0-9a-fA-F-]{36})(?:\/|$)/);
    return match?.[1] ?? null;
  }, [pathname]);
  const effectiveGroupId = workspace === "groups" ? (selectedGroupId ?? routeGroupId) : null;
  const { fullLabel: sheetLabel } = useCurrentLeadSheet();
  const isMobile = useIsMobile();

  const workspaceInfo = useMemo(() => ({
    workspaceType: (workspace === "groups" ? "group" : "personal") as 'personal' | 'group',
    groupId: effectiveGroupId,
    workspaceName: sheetLabel || "Personal",
  }), [workspace, effectiveGroupId, sheetLabel]);

  const { isConnected, isLoading: calSyncLoading, pullChanges } = useGoogleCalendarSync(workspaceInfo);
  const pullRanRef = useRef(false);

  // Pull from Google Calendar when the calendar view opens — catches deletions/edits made externally
  useEffect(() => {
    if (!isOpen || !isConnected || calSyncLoading || leads.length === 0) {
      if (!isOpen) pullRanRef.current = false;
      return;
    }
    if (pullRanRef.current) return;
    pullRanRef.current = true;

    (async () => {
      let anyUpdated = false;
      for (const lead of leads) {
        try {
          const count = await pullChanges(lead.id);
          if (count > 0) anyUpdated = true;
        } catch (err) {
          console.error('Calendar pull error for lead', lead.id, err);
        }
      }
      if (anyUpdated) {
        window.dispatchEvent(new CustomEvent('gantt-items-changed'));
      }
    })();
  }, [isOpen, isConnected, calSyncLoading, leads, pullChanges]);

  // Listen for lead panel width changes
  useEffect(() => {
    const handlePanelChange = (e: Event) => {
      const width = (e as CustomEvent<number>).detail ?? 0;
      setLeadPanelWidth(width);
    };
    window.addEventListener('lead-panel-width-change', handlePanelChange);
    return () => window.removeEventListener('lead-panel-width-change', handlePanelChange);
  }, []);

  const fetchLeads = useCallback(async () => {
    if (initializing) return;
    if (workspace === "groups" && !effectiveGroupId) {
      setLeads([]);
      return;
    }

    const user = await getCurrentUser();
    if (!user) {
      setLeads([]);
      return;
    }

    let query = supabase.from("leads").select("id, lead_name");

    if (workspace === "groups" && effectiveGroupId) {
      query = query.eq("group_id", effectiveGroupId);
    } else {
      query = query.is("group_id", null);
    }

    const { data, error } = await requestWithAuthRecovery(() =>
      query.order("created_at", { ascending: false }),
    );
    if (error) {
      console.error("Error fetching calendar leads:", error);
      return;
    }

    setLeads((data as Lead[]) || []);
  }, [workspace, effectiveGroupId, initializing]);

  // Clear stale leads when scope changes, then fetch immediately
  useEffect(() => {
    if (initializing) return;
    setLeads([]);
    void fetchLeads();
  }, [fetchLeads, initializing]);

  // Listen for manual lead change events
  useEffect(() => {
    const handleChange = () => fetchLeads();
    window.addEventListener('ai-lead-change', handleChange);
    return () => window.removeEventListener('ai-lead-change', handleChange);
  }, [fetchLeads]);

  // Realtime subscription for leads table
  useEffect(() => {
    if (workspace === "groups" && !effectiveGroupId) {
      return;
    }

    const filter = workspace === "groups" && effectiveGroupId
      ? `group_id=eq.${effectiveGroupId}`
      : `group_id=is.null`;

    const channel = supabase
      .channel(`calendar-leads-${workspace}-${effectiveGroupId ?? 'personal'}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'leads', filter },
        () => fetchLeads()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [workspace, effectiveGroupId, fetchLeads]);

  // Realtime subscription for gantt items (so calendar updates when tasks change)
  useEffect(() => {
    if (workspace === "groups" && !effectiveGroupId) {
      return;
    }

    const filter = workspace === "groups" && effectiveGroupId
      ? `group_id=eq.${effectiveGroupId}`
      : `group_id=is.null`;

    const channel = supabase
      .channel(`calendar-gantt-${workspace}-${effectiveGroupId ?? 'personal'}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'lead_gantt_items', filter },
        () => {
          // Dispatch event so LeadCalendarView refetches gantt items
          window.dispatchEvent(new CustomEvent('gantt-items-changed'));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [workspace, effectiveGroupId]);

  const handleItemClick = (leadId: string) => {
    onOpenLead?.(leadId);
  };

  return (
    <>
      {/* Floating Button — positioned above the AI chat button */}
      <Button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-[calc(4rem+env(safe-area-inset-bottom)+88px)] md:bottom-24 z-50 h-11 w-11 rounded-full shadow-lg transition-all duration-300",
          "hover:scale-105 hover:shadow-xl bg-secondary text-secondary-foreground hover:bg-secondary/80",
          isOpen && "scale-0 opacity-0"
        )}
        style={{ right: `${leadPanelWidth + 28}px` }}
        size="icon"
      >
        <CalendarDays className="h-5 w-5" />
      </Button>

      {/* Calendar Panel */}
      <div
        className={cn(
          "fixed z-50 rounded-2xl border bg-background/95 backdrop-blur-sm shadow-2xl transition-all duration-300 flex flex-col",
          isMobile
            ? "bottom-[calc(4rem+env(safe-area-inset-bottom)+8px)] left-2 right-2 max-h-[calc(100vh-7rem)]"
            : "bottom-6 w-[520px] max-h-[calc(100vh-3rem)]",
          isOpen
            ? "translate-y-0 opacity-100 scale-100"
            : "translate-y-4 opacity-0 scale-95 pointer-events-none"
        )}
        style={!isMobile ? { right: `max(8px, ${leadPanelWidth + 24}px)` } : undefined}
      >
        {/* Header with sheet context */}
        <div className="flex items-center justify-between border-b px-3 sm:px-4 py-2 sm:py-2.5 shrink-0">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Calendar</span>
            {sheetLabel && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                {sheetLabel}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-full"
            onClick={() => setIsOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="overflow-auto flex-1 min-h-0">
          <LeadCalendarView leads={leads} active={isOpen} className="border-0 bg-transparent" onItemClick={handleItemClick} />
        </div>
      </div>
    </>
  );
}
